import React, { useEffect } from "react";
import { matchPath, Redirect, Route, Switch, useLocation } from "react-router-dom";

import { Stack } from "@mui/material";
import classNames from "classnames";
import {
  BreadCrumbs,
  BreadcrumbSlotProvider,
  DefaultViewGuard,
  Header,
  ProtectedRoute,
  ReachOut,
  Sidebar
} from "components";
import { DealFormDrawer } from "components/Characteristics";
import { MonitoringInvestor } from "components/guards";
import { CreateMany } from "components/Measure-data";
import { ShareDialog } from "components/modals";
import { BulkUploadController } from "components/Bulk-upload-widget/BulkUploadController";
import { TaskCreate } from "components/Task-manage";
import { Routes } from "constants/routes";
import { useDevice, useNavigate } from "hooks";
import {
  AccountSettings,
  AssetClasses,
  Benchmark,
  ChangePassword,
  Characteristics,
  CloudIntegrations,
  Comparison,
  Dashboard,
  DataRoom,
  DataRoomFile,
  DataRoomsAllFile,
  Deal,
  ExternalReport,
  Freemium,
  Investors,
  Measures,
  Monitoring,
  MyDeals,
  MySecurities,
  ObjectiveDefaults,
  PlaidConnection,
  PortfolioAnalysis,
  Profile,
  ReportGenerator,
  Social,
  SocialProfile,
  Tagger,
  TaskManagement,
  Workspace
} from "pages";
import { NewsFeed } from "pages/News-feed/NewsFeed";
import { PortfolioConstruction } from "pages/Portfolio-construction/PortfolioConstruction";
import { SmartSheet } from "pages/Smart-sheet/SmartSheet";
import { UserManagement } from "pages/User-management/User-management";
import { useAccountSelector, useUserRoleSelector } from "store";
import { IReportType } from "store/types";

import { RoleName } from "../../constants";
import { GPTPrompt } from "../../pages/GPT-prompt/GPTPrompt";

import "./MainRoutes.styles.scss";

export const MainRoutes: React.FC = (): JSX.Element => {
  const { pathname } = useLocation();
  const { isMobile } = useDevice();

  const {
    show_hcs_view,
    enable_user_management,
    show_cashflow,
    is_freemium,
    upgraded_from_freemium,
    show_smartsheet,
    show_ai_report_generator,
    enable_portfolio_construction,
    allow_news: isDashboardEnabled
  } = useAccountSelector() || {};
  const { name: usersRoleName } = useUserRoleSelector() || {};

  const navigate = useNavigate();

  const hideSideBar = show_hcs_view && usersRoleName === RoleName.Investor;

  useEffect(() => {
    if (usersRoleName === RoleName.Investor) {
      navigate({ route: "myDeals" });
    }
  }, [pathname, usersRoleName]);

  return (
    <div className="page main-route">
      {!hideSideBar && !isMobile && <Sidebar />}
      <div
        className="main-route__main"
        {...((hideSideBar || isMobile) && { style: { paddingLeft: "0px", paddingRight: "0px" } })}
      >
        <Header />
        <div
          className={classNames("main-route__switch", {
            "main-route__full-size": matchPath(pathname, Routes.opportunities)
            // matchPath(pathname, Routes.portfolioConstructionInvestor)
          })}
        >
          {/* <div className="main-route__content"> */}
          <BreadcrumbSlotProvider>
            <Stack
              direction="column"
              gap="16px"
              sx={{
                width: "100%",
                height: "100%",
                overflow: matchPath(pathname, Routes.dashboard) ? "auto" : "hidden"
              }}
            >
              <BreadCrumbs />
              <Switch>
                <Route path={Routes.freemium}>
                  {/* <DefaultViewGuard key="freemium" bookmarkType="deal" reportType={null}> */}
                  <ProtectedRoute
                    path={Routes.freemium}
                    availableFor={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
                    redirectTo={{ route: "myDeals", params: {} }}
                    enabled={is_freemium || upgraded_from_freemium}
                  >
                    <Freemium />
                  </ProtectedRoute>
                  {/* </DefaultViewGuard> */}
                </Route>
                <Route exact path={Routes.myDeals}>
                  <DefaultViewGuard key="my-deals" bookmarkType="deal" reportType={null}>
                    <MyDeals />
                  </DefaultViewGuard>
                </Route>
                <Route exact path={Routes.mySecurities} component={MySecurities} />
                <Route exact path={Routes.profile} component={Profile} />
                <Route exact path={Routes.socialProfile} component={SocialProfile} />
                <Route exact path={Routes.social} component={Social} />
                <Route exact path={Routes.changePassword} component={ChangePassword} />
                <Route path={Routes.deal} component={Deal} />
                <Route
                  exact
                  path={[
                    Routes.reports,
                    Routes.timeline,
                    Routes.snapshot,
                    Routes.classic,
                    ...(show_cashflow ? [Routes.cashflow] : [])
                  ]}
                >
                  <DefaultViewGuard key="portfolio-analysis" bookmarkType="report" reportType={IReportType.PORTFOLIO}>
                    <PortfolioAnalysis />
                  </DefaultViewGuard>
                </Route>
                <Route path={Routes.comparison}>
                  <DefaultViewGuard key="comparison" bookmarkType="report" reportType={IReportType.COMPARISON}>
                    <Comparison />
                  </DefaultViewGuard>
                </Route>
                <Route path={Routes.externalReport}>
                  <DefaultViewGuard key="external" bookmarkType="report" reportType={IReportType.EXTERNAL}>
                    <ExternalReport />
                  </DefaultViewGuard>
                </Route>
                <ProtectedRoute
                  path={Routes.reportGenerator}
                  availableFor={[RoleName.Admin, RoleName.Analyst]}
                  enabled={show_ai_report_generator}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <ReportGenerator />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.investorsList}
                  availableFor={[RoleName.Admin]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <Investors />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.measures}
                  availableFor={[RoleName.Admin]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <Measures />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.usersManagement}
                  availableFor={[RoleName.Admin]}
                  redirectTo={{ route: "dashboard", params: {} }}
                  enabled={enable_user_management}
                >
                  <UserManagement />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.characteristics}
                  availableFor={[RoleName.Admin]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <Characteristics />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.gptPrompt}
                  availableFor={[RoleName.Analyst, RoleName.Admin]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <GPTPrompt />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.assets}
                  availableFor={[RoleName.Admin]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <AssetClasses />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.accountSettings}
                  availableFor={[RoleName.Admin]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <AccountSettings />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.objectiveDefaults}
                  availableFor={[RoleName.Admin, RoleName.Analyst]}
                  redirectTo={{ route: "myDeals", params: {} }}
                  enabled={enable_portfolio_construction}
                >
                  <ObjectiveDefaults />
                </ProtectedRoute>
                <Route path={Routes.taskManagement} component={TaskManagement} />
                <Route path={Routes.tagger} component={Tagger} />
                <ProtectedRoute
                  path={Routes.quickView}
                  availableFor={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
                  redirectTo={{ route: "dashboard", params: {} }}
                  enabled={show_smartsheet}
                >
                  <DefaultViewGuard key="quick-view" bookmarkType="quick_view" reportType={null}>
                    <SmartSheet />
                  </DefaultViewGuard>
                </ProtectedRoute>
                <Route path={Routes.benchmark} component={Benchmark} />
                <Route path={Routes.monitoring}>
                  <DefaultViewGuard key="data-operations" bookmarkType="data_operations" reportType={null}>
                    <MonitoringInvestor>
                      <Monitoring />
                    </MonitoringInvestor>
                  </DefaultViewGuard>
                </Route>
                <Route exact path={Routes.plaidConnection} component={PlaidConnection} />
                <Route exact path={[Routes.dataRooms, Routes.plaid]} component={CloudIntegrations} />
                <Route exact path={Routes.dataRoomsAllFiles} component={DataRoomsAllFile} />
                <Route exact path={Routes.dataRoomsFile} component={DataRoomFile} />
                <Route path={Routes.dataRoom} component={DataRoom} />
                <ProtectedRoute
                  exact
                  path={[
                    Routes.portfolioConstruction,
                    Routes.portfolioConstructionInvestor,
                    Routes.portfolioConstructionSlice,
                    Routes.portfolioConstructionInvestorAllocation
                  ]}
                  availableFor={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
                  redirectTo={{ route: "dashboard", params: {} }}
                  enabled={enable_portfolio_construction}
                >
                  <PortfolioConstruction />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.dashboard}
                  availableFor={[RoleName.Analyst, RoleName.Admin, RoleName.TeamViewer]}
                  redirectTo={{ route: "myDeals", params: {} }}
                  enabled={isDashboardEnabled}
                >
                  <Dashboard />
                </ProtectedRoute>
                <ProtectedRoute
                  path={Routes.newsFeed}
                  availableFor={[
                    RoleName.Analyst,
                    RoleName.Admin,
                    RoleName.TeamViewer,
                    RoleName.Viewer,
                    RoleName.Guest
                  ]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <NewsFeed />
                </ProtectedRoute>
                <ProtectedRoute
                  exact
                  path={[
                    Routes.workspace,
                    Routes.workspaceAllFiles,
                    Routes.workspaceRecentlyAdded,
                    Routes.workspaceDeals,
                    Routes.workspaceDeal,
                    Routes.workspaceDealFolder,
                    Routes.workspaceDealFolderDocument,
                    Routes.workspaceDealFolderForm,
                    Routes.workspaceFolder,
                    Routes.workspaceFolderDocument,
                    Routes.workspaceFolderForm,
                    Routes.workspaceFolders
                  ]}
                  availableFor={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
                  redirectTo={{ route: "dashboard", params: {} }}
                >
                  <Workspace />
                </ProtectedRoute>
                <Redirect
                  from={Routes.main}
                  to={is_freemium || upgraded_from_freemium ? Routes.freemiumTools : Routes.dashboard}
                />
              </Switch>
            </Stack>
          </BreadcrumbSlotProvider>
        </div>
      </div>
      <CreateMany />
      {/* <CharacteristicsDeal /> */}
      <DealFormDrawer />
      <ReachOut />
      <ShareDialog />
      <TaskCreate />
      <BulkUploadController />
    </div>
  );
};
